/**
 * PROJET 1 — PAGE PARTENARIAT FOURNISSEURS / DROPSHIPPING
 * Page B2B premium présentée aux compagnies/fournisseurs quand Kru envoie une
 * demande de partenariat ou de dropshipping. Objectif : inspirer confiance,
 * montrer le sérieux, la structure et l'exécution déjà en place.
 * Route publique : /partenariat-fournisseurs
 */
import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { PUBLIC_HOST } from "../lib/domain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNERS = [
  {
    id: "printful",
    brand: "Printful",
    tag: "Dropshipping — impression à la demande",
    status: "Actif · en ligne",
    statusColor: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
    body: "Printful assure déjà la production et le dropshipping de notre gamme textile (t-shirts, hoodies, casquettes) via un système d'envoi automatique relié à nos plateformes.",
    result: "Nous lançons de nouvelles collections sans immobiliser de stock, tout en gardant une image de marque cohérente pour tous nos clubs.",
  },
  {
    id: "hatashita",
    brand: "Hatashita",
    tag: "Compte B2B + dropshipping confirmé",
    status: "Dropshipping accepté",
    statusColor: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
    body: "Hatashita a confirmé notre compte B2B Impact Kick-Boxing et le dropshipping. Partenaire clé pour nos besoins en Judo, BJJ et grappling. Conditions de gros en finalisation.",
    result: "Nous offrons à nos élèves et futurs franchisés une sélection d'équipements techniques de haut niveau, avec une logistique déjà validée.",
  },
  {
    id: "tatami",
    brand: "Tatami Fightwear",
    tag: "Compte « Academy » · 50 % de rabais",
    status: "Academy Account accepté",
    statusColor: "text-amber-300 border-amber-500/40 bg-amber-500/5",
    body: "Tatami nous accorde 50 % de rabais sur leur gamme principale BJJ, dans un modèle spécifique aux académies.",
    result: "Du gear BJJ haut de gamme accessible à nos élèves, sans compromettre notre positionnement premium.",
  },
];

const CATEGORIES = [
  { id: "textile", label: "Textile / vêtements / merchandising" },
  { id: "bjj_grappling", label: "Équipement BJJ / Judo / grappling" },
  { id: "striking", label: "Équipement Muay Thaï / Kickboxing / boxe" },
  { id: "protections", label: "Protections / sécurité / arbitrage" },
  { id: "medailles", label: "Médailles / trophées / événementiel" },
  { id: "autre", label: "Autre" },
];

export default function PartenariatFournisseurs() {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    category: "textile",
    website: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Veuillez remplir l'entreprise, le contact et le courriel.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/partnership/inquiry`, form);
      setResult(data);
      toast.success("Demande envoyée. Nous revenons vers vous rapidement.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Thank you state =====
  if (result?.ok) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-16" data-testid="partenariat-thankyou">
        <div className="max-w-2xl w-full border border-amber-500/40 bg-zinc-950 rounded-2xl p-10 shadow-2xl">
          <div className="text-amber-400 text-xs tracking-[0.3em] mb-4">KAIZEN SYSTEM · PARTENARIAT</div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="partenariat-thankyou-title">
            Demande reçue.
          </h1>
          <p className="text-zinc-300 leading-relaxed mb-4">
            Merci pour votre intérêt à rejoindre l'écosystème Kaizen.
            <br />
            <span className="text-amber-300">Référence : <strong>{result.reference}</strong></span>
          </p>
          <p className="text-zinc-400 text-sm mb-8">{result.message}</p>
          <div className="pt-8 border-t border-zinc-800 text-xs text-zinc-500">
            KAIZEN SYSTEM™ · Impact Kick-Boxing inc. · Réseau Kaizen<br />
            {PUBLIC_HOST} · Val-d'Or, Québec, Canada
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="partenariat-fournisseurs">
      {/* HERO */}
      <section className="relative px-6 sm:px-12 pt-16 pb-24 border-b border-zinc-900 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 18% 25%, #d4af37, transparent 42%), radial-gradient(circle at 82% 75%, #c0392b, transparent 42%)" }} />
        <div className="max-w-6xl mx-auto relative">
          <div className="flex items-center gap-3 mb-10">
            <img
              src="/logos/kaizen_system/generated/kaizen_system_master_color_transparent.png"
              alt="Kaizen System"
              className="h-12 w-12 object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div>
              <div className="text-amber-400 text-[10px] tracking-[0.4em]">KAIZEN SYSTEM™</div>
              <div className="text-zinc-500 text-[10px] tracking-[0.3em]">PARTENARIAT FOURNISSEURS</div>
            </div>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mb-8 tracking-tight" data-testid="partenariat-hero-title">
            Partenariats sélectionnés<br />
            pour un <span className="text-amber-400">réseau martial d'élite.</span>
          </h1>

          <p className="text-xl text-zinc-300 max-w-3xl leading-relaxed mb-6">
            Nous bâtissons un écosystème complet pour les écoles, les athlètes et les franchisés.
            Chaque fournisseur que nous choisissons fait partie d'une stratégie à long terme —
            <strong className="text-white"> qualité, performance et service.</strong>
          </p>
          <p className="text-zinc-400 max-w-3xl leading-relaxed mb-10">
            Nous ne cherchons pas simplement des produits à revendre. Nous cherchons des partenaires
            qui veulent grandir avec nous, dans un système Kaizen structuré, mesurable et scalable.
          </p>

          <a href="#proposer">
            <Button size="lg" className="bg-amber-500 text-black hover:bg-amber-400 font-semibold tracking-wide px-8" data-testid="partenariat-hero-cta">
              Proposer un partenariat →
            </Button>
          </a>
          <div className="text-zinc-500 text-sm mt-3">
            Recevez notre dossier partenaire et planifiez un appel de 30 minutes.
          </div>
        </div>
      </section>

      {/* POURQUOI S'ASSOCIER */}
      <section className="px-6 sm:px-12 py-20 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">01 · POURQUOI S'ASSOCIER AVEC NOUS</div>
          <h2 className="text-3xl sm:text-5xl font-bold mb-12 tracking-tight">
            Pourquoi notre réseau<br />est intéressant pour vous.
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              ["Un modèle hybride", "Écoles physiques, programmes en ligne, événements, circuits amateurs et professionnels."],
              ["Une base d'élèves en croissance", "Encadrée par un système de progression structuré et mesurable."],
              ["Un positionnement premium", "Image, expérience client et standards de service alignés sur le haut de gamme."],
              ["Une vision long terme", "Régional, provincial, national, international — puis mondial."],
              ["Un langage commun", "Performance, discipline, amélioration continue (Kaizen)."],
              ["L'exécution, pas le rêve", "Des partenariats déjà confirmés et des systèmes déjà en opération."],
            ].map(([t, d], i) => (
              <div key={i} className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black" data-testid={`partenariat-why-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="text-amber-400 text-lg shrink-0">✓</span>
                  <div>
                    <div className="text-white font-semibold mb-1">{t}</div>
                    <p className="text-sm text-zinc-400 leading-relaxed">{d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ÉCOSYSTÈME */}
      <section className="px-6 sm:px-12 py-20 border-b border-zinc-900 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">02 · UN ÉCOSYSTÈME CONSTRUIT POUR DURER</div>
          <h2 className="text-3xl sm:text-5xl font-bold mb-6 tracking-tight">Trois axes, une stratégie.</h2>
          <p className="text-zinc-400 max-w-3xl mb-12 leading-relaxed">
            Chaque nouveau partenaire s'intègre dans cet écosystème avec une logique claire :
            créer de la valeur pour nos membres, pour nos clubs et pour nos fournisseurs.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["Athlètes & élèves", "Programmes de progression, équipements adaptés, circuits de compétition."],
              ["Coaches & professeurs", "Formation, outils pédagogiques, parcours de développement."],
              ["Partenaires commerciaux", "Visibilité, volumes progressifs, intégration dans nos canaux physiques et numériques."],
            ].map(([t, d], i) => (
              <div key={i} className="border border-zinc-800 rounded-2xl p-7 bg-black" data-testid={`partenariat-axe-${i}`}>
                <div className="text-amber-400 text-3xl font-bold mb-3">0{i + 1}</div>
                <div className="text-white font-semibold text-lg mb-2">{t}</div>
                <p className="text-sm text-zinc-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTENAIRES DÉJÀ ENGAGÉS */}
      <section className="px-6 sm:px-12 py-20 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">03 · ILS FONT DÉJÀ PARTIE DE L'AVENTURE</div>
          <h2 className="text-3xl sm:text-5xl font-bold mb-4 tracking-tight">Partenaires déjà engagés.</h2>
          <p className="text-zinc-400 max-w-3xl mb-12 leading-relaxed">
            Nous avons déjà sécurisé plusieurs partenariats structurants, qui montrent notre sérieux
            et notre capacité à exécuter.
          </p>
          <div className="space-y-6">
            {PARTNERS.map((p) => (
              <div
                key={p.id}
                className="border border-zinc-800 rounded-2xl p-8 bg-gradient-to-br from-zinc-950 to-black hover:border-amber-500/40 transition-colors"
                data-testid={`partenariat-partner-${p.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">{p.brand}</h3>
                    <div className="text-zinc-500 text-sm mt-1">{p.tag}</div>
                  </div>
                  <span className={`text-xs px-3 py-1.5 rounded-full border self-start ${p.statusColor}`}>
                    {p.status}
                  </span>
                </div>
                <p className="text-zinc-300 leading-relaxed mb-4">{p.body}</p>
                <div className="bg-black border border-zinc-900 rounded-xl p-5">
                  <div className="text-amber-400 text-[11px] tracking-[0.3em] mb-2">RÉSULTAT</div>
                  <p className="text-zinc-300 text-sm leading-relaxed">{p.result}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-zinc-500 italic mt-8 max-w-3xl leading-relaxed">
            Ces premiers partenariats montrent notre capacité à travailler avec des marques sérieuses,
            à structurer des accords clairs et à intégrer leurs produits dans un système qui grandit.
          </p>
        </div>
      </section>

      {/* COMMENT ÇA FONCTIONNE */}
      <section className="px-6 sm:px-12 py-20 border-b border-zinc-900 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">04 · VOTRE PLACE DANS NOTRE SYSTÈME KAIZEN</div>
          <h2 className="text-3xl sm:text-5xl font-bold mb-12 tracking-tight">Un partenariat en 4 phases.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              ["Phase 1 — Alignement", "Comprendre votre gamme, vos priorités, vos contraintes logistiques et vos objectifs de marché."],
              ["Phase 2 — Intégration", "Intégrer vos produits dans nos offres : boutique, programmes, événements, circuits d'athlètes, plateforme."],
              ["Phase 3 — Mesure & ajustement", "Suivre les ventes, les retours terrain et les performances par région. Ajuster ensemble."],
              ["Phase 4 — Expansion", "Quand les résultats sont là, on ouvre progressivement à d'autres villes, régions ou pays, dans un cadre contrôlé."],
            ].map(([t, d], i) => (
              <div key={i} className="border border-zinc-800 rounded-2xl p-6 bg-black" data-testid={`partenariat-phase-${i}`}>
                <div className="text-amber-400 text-2xl font-bold mb-3">0{i + 1}</div>
                <div className="text-white font-semibold mb-2">{t}</div>
                <p className="text-sm text-zinc-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FORM */}
      <section id="proposer" className="px-6 sm:px-12 py-20 bg-black">
        <div className="max-w-3xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">05 · PROPOSER UN PARTENARIAT</div>
          <h2 className="text-3xl sm:text-5xl font-bold mb-3 tracking-tight">Prêt à explorer une collaboration ?</h2>
          <p className="text-zinc-400 mb-10 leading-relaxed">
            Si vous pensez que vos produits peuvent soutenir notre vision et que vous cherchez un partenaire
            structuré, discipliné et en croissance, nous serions heureux d'en parler. Nous vous envoyons un résumé
            de notre modèle et proposons une rencontre de 30 minutes.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="partenariat-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Entreprise *" testid="partenariat-input-company">
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Nom de la compagnie"
                  data-testid="partenariat-input-company-field"
                  required
                />
              </Field>
              <Field label="Personne-contact *" testid="partenariat-input-contact">
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Prénom Nom"
                  data-testid="partenariat-input-contact-field"
                  required
                />
              </Field>
              <Field label="Courriel *" testid="partenariat-input-email">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@entreprise.com"
                  data-testid="partenariat-input-email-field"
                  required
                />
              </Field>
              <Field label="Téléphone" testid="partenariat-input-phone">
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 819 123 4567"
                  data-testid="partenariat-input-phone-field"
                />
              </Field>
              <Field label="Site web" testid="partenariat-input-website">
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://"
                  data-testid="partenariat-input-website-field"
                />
              </Field>
              <Field label="Catégorie de produits" testid="partenariat-input-category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="partenariat-select-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Message (optionnel)" testid="partenariat-input-message">
              <Textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Parlez-nous de votre gamme, vos conditions de gros / dropshipping, vos délais…"
                data-testid="partenariat-input-message-field"
              />
            </Field>

            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold tracking-wide text-lg py-6"
              data-testid="partenariat-submit-button"
            >
              {submitting ? "Envoi en cours…" : "Recevoir notre dossier partenaire →"}
            </Button>
            <p className="text-xs text-zinc-500 text-center">
              Vos données sont traitées par Impact Kick-Boxing inc. (réseau Kaizen) uniquement aux fins de ce partenariat.
            </p>
          </form>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center text-center">
            <a href="mailto:ralph646@hotmail.com">
              <Button variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 px-6" data-testid="partenariat-cta-email">
                ✉️ ralph646@hotmail.com
              </Button>
            </a>
            <a href="tel:+18195279446">
              <Button variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 px-6" data-testid="partenariat-cta-call">
                📞 819-527-9446
              </Button>
            </a>
          </div>
        </div>
      </section>

      <footer className="px-6 sm:px-12 py-10 text-xs text-zinc-600 text-center border-t border-zinc-900">
        KAIZEN SYSTEM™ — Impact Kick-Boxing inc. · Réseau Kaizen · {PUBLIC_HOST}<br />
        Val-d'Or, Québec, Canada · Méthode Kaizen — amélioration continue
      </footer>
    </div>
  );
}

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <Label className="text-zinc-300 text-sm mb-2 block">{label}</Label>
    {children}
  </div>
);
